import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Megaphone,
  Award,
  Recycle,
  Trash2,
  ShieldCheck,
  Rocket,
  ChevronLeft,
  ChevronRight,
  Languages,
  MapPin,
  FolderOpen,
  ChevronDown,
  Search,
  Check,
} from "lucide-react";
import { toast } from "react-hot-toast";
import campaignsService, {
  Campaign,
  CampaignFolder,
  CampaignListParams,
  CampaignMetrics,
} from "../../services/campaigns.service";
import Loader from "../../widgets/Loader";
import Tooltip from "../../widgets/ToolTip";
import {
  SearchFilter,
  MetricCard,
  Dropdown,
  DropdownOption,
  InUseDialog,
} from "../users/ui";

const LANG_LABELS: Record<string, string> = {
  en: "English",
  hi: "Hindi",
  ta: "Tamil",
  te: "Telugu",
  ml: "Malayalam",
  mr: "Marathi",
  bn: "Bengali",
  gu: "Gujarati",
  kn: "Kannada",
};

const LANG_OPTIONS: DropdownOption[] = [
  { value: "", label: "All languages" },
  ...Object.entries(LANG_LABELS).map(([value, label]) => ({ value, label })),
];

const STATUS_OPTIONS: DropdownOption[] = [
  { value: "", label: "All statuses" },
  { value: "draft", label: "Draft" },
  { value: "approved", label: "Approved" },
  { value: "running", label: "Running" },
  { value: "completed", label: "Completed" },
  { value: "archived", label: "Archived" },
];

function CampaignsList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Folder state — keeps the URL bookmark-friendly (?folder=f1)
  const [folders, setFolders] = useState<CampaignFolder[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<string>(
    searchParams.get("folder") || "",
  );
  const [isLoadingFolders, setIsLoadingFolders] = useState(true);

  // Header dropdown state (matches DocumentSearch exactly)
  const [folderDdOpen, setFolderDdOpen] = useState(false);
  const [folderSearch, setFolderSearch] = useState("");
  const folderDdRef = useRef<HTMLDivElement>(null);
  const folderGridSearchRef = useRef<HTMLInputElement>(null);

  // Library state (only meaningful once folder selected)
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [metrics, setMetrics] = useState<CampaignMetrics | null>(null);
  const [isLoadingLibrary, setIsLoadingLibrary] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState({
    name: "",
    language: "",
    status: "",
  });
  const [inUseInfo, setInUseInfo] = useState<{
    name: string;
    message: string;
  } | null>(null);
  const [approveTarget, setApproveTarget] = useState<Campaign | null>(null);

  // ── load folders on mount + click-outside handler for the header dropdown ──
  useEffect(() => {
    (async () => {
      try {
        setIsLoadingFolders(true);
        const list = await campaignsService.listFolders();
        setFolders(list);
      } catch (e) {
        toast.error("Failed to load folders");
      } finally {
        setIsLoadingFolders(false);
      }
    })();
    const onClick = (e: MouseEvent) => {
      if (
        folderDdRef.current &&
        !folderDdRef.current.contains(e.target as Node)
      ) {
        setFolderDdOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // ── load campaigns when folder selected / paging / filters change ──
  useEffect(() => {
    if (!selectedFolderId) return;
    (async () => {
      try {
        setIsLoadingLibrary(true);
        const params: CampaignListParams = {
          skip: (currentPage - 1) * pageSize,
          limit: pageSize,
          document_store_id: selectedFolderId,
          ...(filters.name && { name: filters.name }),
          ...(filters.language && { language: filters.language }),
          ...(filters.status && { status: filters.status }),
        };
        const res = await campaignsService.listCampaigns(params);
        setCampaigns(res.data || []);
        setMetrics(res.metrics_data || null);
      } catch (e: any) {
        toast.error(
          e?.response?.data?.error?.error_description ||
            "Failed to load campaigns",
        );
      } finally {
        setIsLoadingLibrary(false);
      }
    })();
  }, [selectedFolderId, currentPage, filters, pageSize]);

  const handleSelectFolder = (id: string) => {
    setSelectedFolderId(id);
    setSearchParams((p) => {
      const np = new URLSearchParams(p);
      np.set("folder", id);
      return np;
    });
    setFolderDdOpen(false);
    setFolderSearch("");
    setCurrentPage(1);
    setFilters({ name: "", language: "", status: "" });
  };

  const handleApprove = (c: Campaign) => setApproveTarget(c);

  const confirmApprove = async () => {
    if (!approveTarget) return;
    const c = approveTarget;
    setApproveTarget(null);
    try {
      await campaignsService.approveCampaign(c._id);
      toast.success(`"${c.name}" approved as Gold`);
      reloadLibrary();
    } catch {
      toast.error("Failed to approve");
    }
  };

  const handleDelete = async (c: Campaign) => {
    if ((c.reuses_count || 0) > 0) {
      setInUseInfo({
        name: c.name,
        message: `This campaign has been reused ${c.reuses_count} time(s). Reusing campaigns cannot be deleted without breaking historical lineage.`,
      });
      return;
    }
    try {
      await campaignsService.deleteCampaign(c._id);
      toast.success("Campaign deleted");
      reloadLibrary();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const reloadLibrary = () => setCurrentPage((p) => p); // forces effect via reference

  const filteredFolders = folderSearch.trim()
    ? folders.filter((f) =>
        f.name.toLowerCase().includes(folderSearch.toLowerCase()),
      )
    : folders;

  const selectedFolder = folders.find((f) => f._id === selectedFolderId);

  const isFiltering = Boolean(
    filters.name || filters.language || filters.status,
  );
  const totalCount = isFiltering
    ? campaigns.length
    : (metrics?.total_campaigns ?? 0);
  const totalPages = Math.ceil(totalCount / pageSize);
  const startItem = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="h-full min-h-screen flex flex-col bg-slate-50">
      {/* HEADER — title always visible, folder dropdown shows AFTER selection */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 z-40 sticky top-0">
        <div className="px-5 py-3 flex items-center gap-5">
          {/* Title */}
          <div className="flex items-center gap-2.5 flex-shrink-0">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-600">
              <Megaphone className="h-4 w-4 text-white" />
            </div>
            <div className="hidden lg:block">
              <h1 className="text-sm font-bold text-slate-900 leading-tight flex items-center gap-2">
                AI Decision Intelligence
                <span className="text-[9px] font-semibold uppercase tracking-wider bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">
                  New
                </span>
              </h1>
              <p className="text-[10px] text-slate-500">
                Cross-lingual campaign reuse with insights and decision support
              </p>
            </div>
          </div>

          {/* Folder dropdown — visible only AFTER folder picked */}
          {selectedFolderId && (
            <div className="w-full max-w-xs" ref={folderDdRef}>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setFolderDdOpen(!folderDdOpen)}
                  className={`relative w-full text-left px-3 py-2 pl-9 bg-white border text-sm text-slate-900 focus:outline-none transition-all font-medium flex items-center justify-between rounded-md
                    ${folderDdOpen ? "border-blue-500 ring-1 ring-blue-500" : "border-slate-200 hover:border-blue-300"}`}
                >
                  <span className="truncate block pr-4">
                    {selectedFolder?.name}
                  </span>
                  <FolderOpen className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-amber-500" />
                  <ChevronDown
                    className={`absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 text-slate-400 transition-transform ${folderDdOpen ? "rotate-180" : ""}`}
                  />
                </button>
                {folderDdOpen && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-xl">
                    <div className="p-2 border-b border-slate-100">
                      <div className="relative">
                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-slate-400" />
                        <input
                          type="text"
                          value={folderSearch}
                          onChange={(e) => setFolderSearch(e.target.value)}
                          placeholder="Search folders..."
                          className="w-full pl-7 pr-2 py-1 text-xs border border-slate-200 rounded bg-slate-50 focus:outline-none focus:border-blue-400 placeholder:text-slate-400"
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>
                    <ul className="max-h-60 overflow-y-auto py-1">
                      {filteredFolders.length === 0 ? (
                        <li className="px-3 py-3 text-center text-xs text-slate-500">
                          No folders found
                        </li>
                      ) : (
                        filteredFolders.map((f) => {
                          const isSel = selectedFolderId === f._id;
                          return (
                            <li
                              key={f._id}
                              onClick={() => handleSelectFolder(f._id)}
                              className={`px-3 py-1.5 cursor-pointer flex items-center justify-between text-xs transition-colors
                                ${isSel ? "bg-blue-50 text-blue-700 font-medium" : "text-slate-700 hover:bg-slate-50"}`}
                            >
                              <span className="truncate">{f.name}</span>
                              {isSel && (
                                <Check className="h-3 w-3 text-blue-600" />
                              )}
                            </li>
                          );
                        })
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Right-side actions when folder selected */}
          {selectedFolderId && (
            <div className="flex items-center gap-2 ml-auto">
              <SearchFilter
                placeholder="Search campaigns…"
                value={filters.name}
                onChange={(v) => {
                  setFilters((x) => ({ ...x, name: v }));
                  setCurrentPage(1);
                }}
                className="w-56"
              />
              <button
                onClick={() =>
                  navigate(`/campaigns/search?folder=${selectedFolderId}`)
                }
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
                type="button"
              >
                <Rocket className="w-4 h-4" />
                New Draft
              </button>
            </div>
          )}
        </div>
      </div>

      {/* CONTENT AREA */}
      <div className="flex-1 overflow-hidden relative min-h-0">
        {isLoadingFolders ? (
          <div className="h-full flex items-center justify-center">
            <Loader />
          </div>
        ) : !selectedFolderId ? (
          /* ─── EMPTY STATE: folder grid (matches DocumentSearch's look) ─── */
          <div className="h-full overflow-y-auto py-4">
            {/* "How it works" — 3-step quick guide for first-time users */}
            <div className="mb-5 bg-gradient-to-r from-blue-50 to-violet-50 border border-blue-100 rounded-md p-4">
              <p className="text-[11px] font-semibold uppercase text-blue-700 tracking-wider mb-3">
                How it works
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="flex items-start gap-3">
                  <span className="shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">
                    1
                  </span>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Pick a folder
                    </p>
                    <p className="text-[11px] text-gray-600 mt-0.5">
                      Folders group related campaigns. Everything below is
                      scoped to the folder you pick.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">
                    2
                  </span>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Search in any language
                    </p>
                    <p className="text-[11px] text-gray-600 mt-0.5">
                      Type a Tamil prompt — it can find matching Hindi or
                      English campaigns inside this folder.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">
                    3
                  </span>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Generate a localized draft
                    </p>
                    <p className="text-[11px] text-gray-600 mt-0.5">
                      Pick a source from the results. The Workspace creates a
                      draft in your target language with citations and insights.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between mb-5">
              <div>
                <p className="text-sm font-semibold text-slate-800">
                  Select a folder to get started
                </p>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  Pick a campaign folder to view its library and start a new
                  cross-lingual draft
                </p>
              </div>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                <input
                  ref={folderGridSearchRef}
                  type="text"
                  className="w-full pl-9 pr-3 py-2 text-xs border border-slate-200 rounded-md bg-white focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 placeholder:text-slate-400"
                  placeholder="Search folders..."
                  value={folderSearch}
                  onChange={(e) => setFolderSearch(e.target.value)}
                />
              </div>
            </div>

            {filteredFolders.length === 0 ? (
              <div className="py-16 text-center">
                <FolderOpen className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-400">
                  {folderSearch
                    ? "No folders match your search."
                    : "No folders yet — upload documents in the Documents page first."}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
                {filteredFolders.map((f) => (
                  <button
                    key={f._id}
                    onClick={() => handleSelectFolder(f._id)}
                    className="group flex flex-col items-center text-center p-3 bg-white border border-slate-200 rounded-md hover:border-blue-300 hover:shadow transition-all"
                  >
                    {/* Classic folder glyph (yellow) — matches DocumentSearch */}
                    <div className="relative mb-2">
                      <div className="w-10 h-8 bg-amber-400 rounded-t-sm rounded-b-md relative group-hover:bg-amber-500 transition-colors">
                        <div className="absolute -top-1 left-0 w-5 h-2 bg-amber-400 rounded-t-sm group-hover:bg-amber-500 transition-colors" />
                      </div>
                    </div>
                    <p className="text-[11px] font-medium text-slate-700 group-hover:text-blue-700 truncate w-full leading-tight">
                      {f.name}
                    </p>
                    <p className="text-[10px] text-slate-400 mt-0.5">
                      {f.campaign_count} campaign
                      {f.campaign_count === 1 ? "" : "s"} · {f.language_count}{" "}
                      lang
                    </p>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* ─── FOLDER SELECTED: full library ─── */
          <div className="h-full overflow-y-auto">
            {/* Metric cards */}
            <div className="pt-4 pb-2">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <MetricCard
                  icon={<Megaphone className="w-5 h-5" />}
                  label="Total Campaigns"
                  value={metrics?.total_campaigns ?? 0}
                  accent="blue"
                  isLoading={!metrics && isLoadingLibrary}
                />
                <MetricCard
                  icon={<Award className="w-5 h-5" />}
                  label="Gold (Reusable)"
                  value={metrics?.gold_campaigns ?? 0}
                  sub={
                    metrics
                      ? `${Math.round(metrics.total_campaigns ? (metrics.gold_campaigns / metrics.total_campaigns) * 100 : 0)}% of folder`
                      : undefined
                  }
                  accent="emerald"
                  isLoading={!metrics && isLoadingLibrary}
                />
                <MetricCard
                  icon={<Recycle className="w-5 h-5" />}
                  label="Reuses This Month"
                  value={metrics?.reuses_this_month ?? 0}
                  accent="violet"
                  isLoading={!metrics && isLoadingLibrary}
                />
              </div>
            </div>

            {/* Filters + Table */}
            <div className="pb-6">
              <div className="bg-white rounded-md border border-gray-200 overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <h3 className="text-sm font-semibold text-gray-900">
                    Campaign Library
                  </h3>
                  <div className="flex items-center gap-2">
                    <Dropdown
                      options={LANG_OPTIONS}
                      value={filters.language}
                      onChange={(v) => {
                        setFilters((f) => ({ ...f, language: v }));
                        setCurrentPage(1);
                      }}
                      placeholder="Language"
                      className="w-36"
                    />
                    <Dropdown
                      options={STATUS_OPTIONS}
                      value={filters.status}
                      onChange={(v) => {
                        setFilters((f) => ({ ...f, status: v }));
                        setCurrentPage(1);
                      }}
                      placeholder="Status"
                      className="w-36"
                    />
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Campaign
                        </th>
                        <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Language
                        </th>
                        <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Region
                        </th>
                        <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          <Tooltip content="Status — Draft (not yet approved), Approved (ready for cross-lingual reuse), Running (live now), Completed (finished, KPIs available), Archived (hidden).">
                            <span className="cursor-help underline decoration-dotted decoration-gray-400 underline-offset-4">
                              Status
                            </span>
                          </Tooltip>
                        </th>
                        <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          <Tooltip content="Click-Through Rate — % of viewers who clicked the campaign creative. Higher = more compelling.">
                            <span className="cursor-help underline decoration-dotted decoration-gray-400 underline-offset-4">
                              CTR
                            </span>
                          </Tooltip>
                        </th>
                        <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          <Tooltip content="Reuses — how many times this campaign was used as a source to generate a draft in another language. High reuse = trusted asset.">
                            <span className="cursor-help underline decoration-dotted decoration-gray-400 underline-offset-4">
                              Reuses
                            </span>
                          </Tooltip>
                        </th>
                        <th className="px-5 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {isLoadingLibrary ? (
                        <tr>
                          <td colSpan={7} className="px-5 py-12 text-center">
                            <Loader />
                          </td>
                        </tr>
                      ) : campaigns.length === 0 ? (
                        <tr>
                          <td
                            colSpan={7}
                            className="px-5 py-12 text-center text-gray-500"
                          >
                            No campaigns in this folder yet. Click{" "}
                            <span className="font-medium text-blue-600">
                              New Draft
                            </span>{" "}
                            to start.
                          </td>
                        </tr>
                      ) : (
                        campaigns.map((c) => (
                          <tr
                            key={c._id}
                            className="hover:bg-gray-50 transition-colors"
                          >
                            <td className="px-5 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center font-semibold text-sm">
                                  {c.name.charAt(0).toUpperCase()}
                                </div>
                                <div>
                                  <div className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                                    {c.name}
                                    {c.is_gold && (
                                      <Tooltip content="Gold — approved for cross-lingual reuse">
                                        <span>
                                          <Award className="w-3.5 h-3.5 text-amber-500" />
                                        </span>
                                      </Tooltip>
                                    )}
                                  </div>
                                  <div className="text-[11px] text-gray-500">
                                    by {c.created_by}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-5 py-4 whitespace-nowrap">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 rounded">
                                <Languages className="w-3 h-3" />
                                {LANG_LABELS[c.language] || c.language}
                              </span>
                            </td>
                            <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-600">
                              <span className="inline-flex items-center gap-1">
                                <MapPin className="w-3 h-3 text-gray-400" />
                                {c.region || "—"}
                              </span>
                            </td>
                            <td className="px-5 py-4 whitespace-nowrap">
                              <StatusPill status={c.status} />
                            </td>
                            <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-700">
                              {c.kpis?.ctr ? `${c.kpis.ctr}%` : "—"}
                            </td>
                            <td className="px-5 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${(c.reuses_count || 0) > 0 ? "bg-violet-50 text-violet-700" : "bg-gray-100 text-gray-500"}`}
                              >
                                <Recycle className="w-3 h-3" />
                                {c.reuses_count || 0}
                              </span>
                            </td>
                            <td className="px-5 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex items-center justify-end gap-1">
                                <Tooltip content="Open Workspace">
                                  <button
                                    onClick={() =>
                                      navigate(
                                        `/campaigns/${c._id}/workspace?folder=${selectedFolderId}`,
                                      )
                                    }
                                    className="text-blue-600 hover:bg-blue-50 p-2 rounded-md transition-colors"
                                    type="button"
                                  >
                                    <Rocket className="w-4 h-4" />
                                  </button>
                                </Tooltip>
                                {!c.is_gold && (
                                  <Tooltip content="Approve as Gold">
                                    <button
                                      onClick={() => handleApprove(c)}
                                      className="text-amber-600 hover:bg-amber-50 p-2 rounded-md transition-colors"
                                      type="button"
                                    >
                                      <ShieldCheck className="w-4 h-4" />
                                    </button>
                                  </Tooltip>
                                )}
                                <Tooltip content="Delete">
                                  <button
                                    onClick={() => handleDelete(c)}
                                    className="text-red-600 hover:bg-red-50 p-2 rounded-md transition-colors"
                                    type="button"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </Tooltip>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {totalCount > 0 && (
                  <div className="px-5 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="text-sm text-gray-700">
                        Showing <span className="font-medium">{startItem}</span>{" "}
                        to <span className="font-medium">{endItem}</span> of{" "}
                        <span className="font-medium">{totalCount}</span>{" "}
                        results
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">Show:</span>
                        <select
                          value={pageSize}
                          onChange={(e) => {
                            setPageSize(Number(e.target.value));
                            setCurrentPage(1);
                          }}
                          className="px-2 py-1 border border-gray-300 rounded-md text-sm"
                        >
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                          <option value={50}>50</option>
                        </select>
                      </div>
                    </div>
                    {totalPages > 1 && (
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => setCurrentPage((p) => p - 1)}
                          disabled={currentPage === 1}
                          className="inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </button>
                        {Array.from(
                          { length: Math.min(5, totalPages) },
                          (_, i) => {
                            let n = i + 1;
                            if (totalPages > 5 && currentPage > 3)
                              n = Math.min(totalPages - 4 + i, totalPages);
                            if (
                              totalPages > 5 &&
                              currentPage > 3 &&
                              currentPage < totalPages - 2
                            )
                              n = currentPage - 2 + i;
                            return (
                              <button
                                key={n}
                                onClick={() => setCurrentPage(n)}
                                className={`inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === n ? "z-10 bg-blue-50 border-blue-500 text-blue-600" : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"}`}
                              >
                                {n}
                              </button>
                            );
                          },
                        )}
                        <button
                          onClick={() => setCurrentPage((p) => p + 1)}
                          disabled={currentPage === totalPages}
                          className="inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <InUseDialog
        isOpen={!!inUseInfo}
        onClose={() => setInUseInfo(null)}
        title={`Cannot delete "${inUseInfo?.name ?? ""}"`}
        message={inUseInfo?.message || ""}
        hints={[
          "Archive the campaign instead — keeps lineage for past generations intact.",
          "Or remove the dependent generations first, then retry delete.",
        ]}
      />

      {approveTarget && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setApproveTarget(null)}
        >
          <div
            className="bg-white rounded-md shadow-xl max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-gray-200 flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-amber-500" />
              <h3 className="text-sm font-semibold text-gray-900">
                Approve as Gold?
              </h3>
            </div>
            <div className="p-5 text-sm text-gray-700 space-y-2">
              <p>
                Approving <strong>"{approveTarget.name}"</strong> as Gold makes
                it <strong>discoverable to other regions</strong> for
                cross-lingual reuse.
              </p>
              <p className="text-xs text-gray-500">
                This is governance — only Gold campaigns appear in cross-lingual
                search. You can un-approve later by archiving.
              </p>
            </div>
            <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={() => setApproveTarget(null)}
                className="px-3 py-1.5 text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 rounded"
              >
                Cancel
              </button>
              <button
                onClick={confirmApprove}
                className="px-3 py-1.5 text-xs font-medium bg-amber-500 hover:bg-amber-600 text-white rounded inline-flex items-center gap-1.5"
              >
                <ShieldCheck className="w-3.5 h-3.5" />
                Approve as Gold
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }: { status: Campaign["status"] }) {
  const map: Record<
    Campaign["status"],
    { bg: string; dot: string; label: string; hint: string }
  > = {
    draft: {
      bg: "bg-gray-100 text-gray-700",
      dot: "bg-gray-400",
      label: "Draft",
      hint: "Draft — not yet approved. Won't appear in cross-lingual search.",
    },
    approved: {
      bg: "bg-blue-50 text-blue-700",
      dot: "bg-blue-500",
      label: "Approved",
      hint: "Approved — ready to be picked up as a source by other regions.",
    },
    running: {
      bg: "bg-emerald-50 text-emerald-700",
      dot: "bg-emerald-500",
      label: "Running",
      hint: "Running — currently live in a region.",
    },
    completed: {
      bg: "bg-violet-50 text-violet-700",
      dot: "bg-violet-500",
      label: "Completed",
      hint: "Completed — run finished. KPIs available for performance insights.",
    },
    archived: {
      bg: "bg-slate-100 text-slate-600",
      dot: "bg-slate-400",
      label: "Archived",
      hint: "Archived — hidden from search and lists.",
    },
  };
  const v = map[status] || map.draft;
  return (
    <Tooltip content={v.hint}>
      <span
        className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-full cursor-help ${v.bg}`}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${v.dot}`} />
        {v.label}
      </span>
    </Tooltip>
  );
}

export default CampaignsList;
